import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Loader2, AlertTriangle, RotateCcw, Maximize2, EyeOff, Eye } from "lucide-react";
import initOpenCascade from "occt-import-js";

/**
 * Step3DViewer — in-browser WebGL viewer for STEP/IGES files.
 *
 * Pipeline:
 *   raw bytes → occt-import-js (WASM) → triangulated meshes
 *             → three.js BufferGeometry → interactive WebGL scene
 *
 * Features:
 *   - Pan / zoom / rotate via OrbitControls.
 *   - Per-mesh material with edge wireframe for clarity.
 *   - DFM overlay: highlights detected holes from the backend feature
 *     analysis (candidate_holes) as transparent cylinders.
 *   - Toggle wireframe / overlay layers.
 */

// Cache the WASM module across remounts.
let _occtPromise = null;
function getOcct() {
  if (!_occtPromise) {
    _occtPromise = initOpenCascade({
      locateFile: (file) => {
        if (file.endsWith(".wasm")) {
          return "/occt-import-js.wasm";
        }
        return file;
      },
    });
  }
  return _occtPromise;
}

function buildScene(container, theme = "dark") {
  const scene = new THREE.Scene();
  if (theme === "light") {
    // Fictiv-style soft gradient background painted onto a fullscreen plane.
    scene.background = new THREE.Color(0xeef2f7);
  } else {
    scene.background = new THREE.Color(0x0b1220);
  }

  // Lights — three-point for clean CAD-style shading.
  const ambient = new THREE.AmbientLight(0xffffff, theme === "light" ? 0.85 : 0.55);
  scene.add(ambient);
  const key = new THREE.DirectionalLight(0xffffff, theme === "light" ? 0.65 : 0.85);
  key.position.set(1, 1, 1);
  scene.add(key);
  const fill = new THREE.DirectionalLight(theme === "light" ? 0xeaf0ff : 0x88aaff, 0.35);
  fill.position.set(-1, 0.5, -1);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(theme === "light" ? 0xffffff : 0xfff0c0, 0.25);
  rim.position.set(0, -1, 0.5);
  scene.add(rim);

  // Grid floor for depth cue.
  const gridColors = theme === "light"
    ? [0xc8d2e0, 0xdce3ee]
    : [0x334155, 0x1e293b];
  const grid = new THREE.GridHelper(400, 40, gridColors[0], gridColors[1]);
  grid.position.y = -0.01;
  scene.add(grid);

  const camera = new THREE.PerspectiveCamera(
    35,
    container.clientWidth / container.clientHeight,
    0.1,
    20000
  );
  camera.position.set(120, 120, 200);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = 5;
  controls.maxDistance = 4000;

  return { scene, camera, renderer, controls, theme };
}

function meshFromOcct(meshData) {
  const geom = new THREE.BufferGeometry();
  const positions = new Float32Array(meshData.attributes.position.array);
  geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  if (meshData.attributes.normal) {
    const normals = new Float32Array(meshData.attributes.normal.array);
    geom.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  } else {
    geom.computeVertexNormals();
  }
  if (meshData.index && meshData.index.array) {
    const idxArr = meshData.index.array;
    // OCCT may return 32-bit indices (large parts). Pick the right typed array.
    const indices =
      idxArr instanceof Uint32Array || positions.length / 3 > 65535
        ? new Uint32Array(idxArr)
        : new Uint16Array(idxArr);
    geom.setIndex(new THREE.BufferAttribute(indices, 1));
  }
  return geom;
}

function disposeAll(group) {
  group.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
      else obj.material.dispose();
    }
  });
}

function fitCameraToObject(camera, controls, object) {
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;
  const size = new THREE.Vector3();
  box.getSize(size);
  const center = new THREE.Vector3();
  box.getCenter(center);
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const dist = (maxDim / 2) / Math.tan(fov / 2) * 1.8;
  const dir = new THREE.Vector3(1, 0.6, 1).normalize();
  camera.position.copy(center).add(dir.multiplyScalar(dist));
  camera.near = Math.max(0.1, dist / 1000);
  camera.far = dist * 50;
  camera.updateProjectionMatrix();
  controls.target.copy(center);
  controls.update();
}

function buildHoleOverlay(holes, partBBox, color = 0x22d3ee) {
  const group = new THREE.Group();
  group.name = "hole-overlay";
  if (!holes || holes.length === 0) return group;
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  // Two modes:
  //   (a) backend provided center_xyz + axis_endpoints (newer step_analyzer)
  //       → place semi-transparent cylinder AT THE HOLE LOCATION on the model
  //   (b) older payload without positions → fall back to the legend stack
  //       beside the model (visual reference only).
  const hasPositions = holes.some((h) => Array.isArray(h.center_xyz));
  if (!hasPositions) {
    if (!partBBox) return group;
    const { length, width } = partBBox;
    let yOff = 0;
    holes.slice(0, 12).forEach((h, i) => {
      const r = Math.max(h.diameter_mm / 2, 0.5);
      const depth = Math.max(h.depth_mm, 1);
      const geom = new THREE.CylinderGeometry(r, r, depth, 32, 1, true);
      const mesh = new THREE.Mesh(geom, mat.clone());
      mesh.position.set(length / 2 + 20 + r, yOff + depth / 2, -width / 2 - 20);
      mesh.userData = { hole: h, index: i, mode: "legend" };
      group.add(mesh);
      yOff += depth + 4;
    });
    return group;
  }

  // Mode (a) — real placement at hole XYZ centroid + axis-endpoints.
  // STEP files use Z-up while our scene rotates by -90° around X to match
  // Three's default Y-up. The hole-overlay group is added to the scene
  // *with the same rotation as the model* so we can keep coordinates in
  // the original STEP frame here.
    holes.slice(0, 24).forEach((h, i) => {
      const r = Math.max(h.diameter_mm / 2, 0.3);
      const depth = Math.max(h.depth_mm, 1);
      const geom = new THREE.CylinderGeometry(r, r, depth * 1.05, 48, 1, true);
      const mesh = new THREE.Mesh(geom, mat.clone());

    // Three's default cylinder is along +Y. Rotate so it aligns with the
    // detected hole axis direction. Backend already normalised the sign.
    const dir = h.axis_direction || [0, 0, 1];
    const targetAxis = new THREE.Vector3(dir[0], dir[1], dir[2]).normalize();
    const up = new THREE.Vector3(0, 1, 0);
    const q = new THREE.Quaternion().setFromUnitVectors(up, targetAxis);
    mesh.quaternion.copy(q);

    // Place the cylinder centroid at the detected centre.
    const c = h.center_xyz;
    mesh.position.set(c[0], c[1], c[2]);

    // Slightly bigger envelope edge to highlight visibility on the model.
    const edgeMat = new THREE.LineBasicMaterial({
      color: 0xfde047,
      transparent: true,
      opacity: 0.95,
    });
    const ringGeom = new THREE.EdgesGeometry(geom, 18);
    const ring = new THREE.LineSegments(ringGeom, edgeMat);
    ring.quaternion.copy(q);
    ring.position.copy(mesh.position);

    mesh.userData = { hole: h, index: i, mode: "placed" };
    group.add(mesh);
    group.add(ring);
  });
  return group;
}

export default function Step3DViewer({ quoteId, rawBytes = null, holes = [], partBBox = null, height = 560, theme = "dark", hovered = null }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const sceneRefs = useRef(null);
  const modelGroupRef = useRef(null);
  const overlayRef = useRef(null);
  const animFrameRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");
  const [meshCount, setMeshCount] = useState(0);
  const [showWireframe, setShowWireframe] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  // Initialize scene
  useEffect(() => {
    if (!containerRef.current) return;
    const refs = buildScene(containerRef.current, theme);
    sceneRefs.current = refs;

    const tick = () => {
      animFrameRef.current = requestAnimationFrame(tick);
      refs.controls.update();
      refs.renderer.render(refs.scene, refs.camera);
    };
    tick();

    const onResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      refs.camera.aspect = w / h;
      refs.camera.updateProjectionMatrix();
      refs.renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (modelGroupRef.current) disposeAll(modelGroupRef.current);
      if (overlayRef.current) disposeAll(overlayRef.current);
      refs.renderer.dispose();
      if (refs.renderer.domElement.parentNode) {
        refs.renderer.domElement.parentNode.removeChild(refs.renderer.domElement);
      }
    };
    // Scene is built once on mount; theme is captured by closure. Changing
    // theme at runtime is not supported (and not needed in our usage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load + parse STEP file (from server endpoint OR from in-memory raw bytes).
  const loadModel = useCallback(async () => {
    if (!sceneRefs.current) return;
    if (!quoteId && !rawBytes) return;
    setStatus("loading");
    setError("");
    try {
      let buf;
      if (rawBytes) {
        // In-memory bytes path (used in the upload wizard before the quote
        // is created). Avoids a server roundtrip.
        buf = rawBytes instanceof Uint8Array ? rawBytes : new Uint8Array(rawBytes);
      } else {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        const resp = await fetch(`${apiBase}/api/quotes/${quoteId}/cad-file`, {
          credentials: "include",
        });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        buf = new Uint8Array(await resp.arrayBuffer());
      }

      const occt = await getOcct();
      const result = occt.ReadStepFile(buf, null);
      if (!result || !result.success) {
        throw new Error("OCCT non è riuscito a leggere il file STEP");
      }
      const { scene } = sceneRefs.current;

      // Clean previous model
      if (modelGroupRef.current) {
        scene.remove(modelGroupRef.current);
        disposeAll(modelGroupRef.current);
      }
      const group = new THREE.Group();
      group.name = "cad-model";
      // Match palette to scene theme for visual harmony.
      const palette = theme === "light"
        ? [0x4a90e2, 0x10b981, 0xf59e0b, 0x8b5cf6, 0xec4899]
        : [0x60a5fa, 0x34d399, 0xf59e0b, 0xa78bfa, 0xf472b6];
      let total = 0;
      result.meshes.forEach((m, i) => {
        const geom = meshFromOcct(m);
        const color = palette[i % palette.length];
        const mat = new THREE.MeshPhongMaterial({
          color,
          shininess: 60,
          flatShading: false,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geom, mat);
        // STEP files use Z-up; three.js default is Y-up. Rotate -90° around X.
        mesh.rotation.x = -Math.PI / 2;
        group.add(mesh);

        // Edge wireframe overlay
        const edges = new THREE.EdgesGeometry(geom, 18);
        const lineMat = new THREE.LineBasicMaterial({
          color: theme === "light" ? 0x334155 : 0x0f172a,
          transparent: true,
          opacity: theme === "light" ? 0.6 : 0.45,
        });
        const wire = new THREE.LineSegments(edges, lineMat);
        wire.rotation.x = -Math.PI / 2;
        wire.userData.isWireframe = true;
        group.add(wire);

        total += 1;
      });
      modelGroupRef.current = group;
      scene.add(group);
      setMeshCount(total);

      // DFM overlay (holes)
      if (overlayRef.current) {
        scene.remove(overlayRef.current);
        disposeAll(overlayRef.current);
      }
      const overlay = buildHoleOverlay(holes, partBBox);
      overlay.rotation.x = -Math.PI / 2;
      overlayRef.current = overlay;
      scene.add(overlay);

      fitCameraToObject(sceneRefs.current.camera, sceneRefs.current.controls, group);
      setStatus("ready");
    } catch (e) {
      console.error("STEP load failed:", e);
      setError(e.message || String(e));
      setStatus("error");
    }
  }, [quoteId, rawBytes, holes, partBBox, theme]);

  useEffect(() => {
    loadModel();
  }, [loadModel]);

  // Wireframe toggle
  useEffect(() => {
    if (!modelGroupRef.current) return;
    modelGroupRef.current.traverse((o) => {
      if (o.userData?.isWireframe) o.visible = showWireframe;
    });
  }, [showWireframe, meshCount]);

  // Overlay toggle
  useEffect(() => {
    if (overlayRef.current) overlayRef.current.visible = showOverlay;
  }, [showOverlay, meshCount]);

  // Hover sync: when the parent reports `hovered === "hole-<index>"`, brighten
  // the matching overlay cylinder. Other cylinders dim.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const wantHoleIdx = (() => {
      if (!hovered || typeof hovered !== "string") return null;
      const m = hovered.match(/^hole-(\d+)$/);
      return m ? parseInt(m[1], 10) : null;
    })();
    overlay.traverse((obj) => {
      if (!obj.userData?.mode === "placed") return;
      const idx = obj.userData?.index;
      if (idx == null || !obj.material) return;
      const isMatch = wantHoleIdx === idx;
      const isLine = obj.type === "LineSegments";
      if (wantHoleIdx == null) {
        // No hover — restore defaults
        obj.material.opacity = isLine ? 0.95 : 0.45;
      } else {
        obj.material.opacity = isMatch ? (isLine ? 1.0 : 0.85) : (isLine ? 0.25 : 0.12);
      }
      obj.material.needsUpdate = true;
    });
  }, [hovered, meshCount]);

  const handleReset = () => {
    if (!modelGroupRef.current || !sceneRefs.current) return;
    fitCameraToObject(sceneRefs.current.camera, sceneRefs.current.controls, modelGroupRef.current);
  };

  const isLight = theme === "light";
  return (
    <div
      className={
        isLight
          ? "rounded-xl border border-slate-300 bg-white overflow-hidden shadow-sm"
          : "tech-card overflow-hidden"
      }
      data-testid="step-3d-viewer"
    >
      <div className={`px-5 py-3 border-b flex items-center justify-between ${isLight ? "border-slate-200 bg-slate-50" : "border-slate-700/60"}`}>
        <div className={`flex items-center gap-2 text-xs font-mono uppercase tracking-wider ${isLight ? "text-slate-700" : "text-slate-300"}`}>
          <Maximize2 size={12} className={isLight ? "text-blue-600" : "text-cyan-400"} />
          Viewer 3D interattivo
          {meshCount > 0 && (
            <span className={`ml-2 text-[10px] ${isLight ? "text-slate-500" : "text-slate-500"}`}>
              · {meshCount} {meshCount === 1 ? "solido" : "solidi"} · {holes.length} fori AI
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowWireframe((v) => !v)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${
              showWireframe
                ? isLight
                  ? "border-blue-500/60 text-blue-700 bg-blue-50"
                  : "border-cyan-500/60 text-cyan-300 bg-cyan-500/10"
                : isLight
                ? "border-slate-300 text-slate-500 hover:border-slate-400"
                : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
            data-testid="toggle-wireframe"
            title={t("viewerTab.toggleEdges")}
          >
            {showWireframe ? <Eye size={11}/> : <EyeOff size={11}/>} Spigoli
          </button>
          {holes.length > 0 && (
            <button
              onClick={() => setShowOverlay((v) => !v)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${
                showOverlay
                  ? isLight
                    ? "border-emerald-500/60 text-emerald-700 bg-emerald-50"
                    : "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                  : isLight
                  ? "border-slate-300 text-slate-500 hover:border-slate-400"
                  : "border-slate-700 text-slate-400 hover:border-slate-500"
              }`}
              data-testid="toggle-dfm-overlay"
              title={t("viewerTab.toggleHolesOverlay")}
            >
              <AlertTriangle size={11}/> DFM ({holes.length})
            </button>
          )}
          <button
            onClick={handleReset}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${
              isLight
                ? "border-slate-300 text-slate-600 hover:border-slate-400"
                : "border-slate-700 text-slate-400 hover:border-slate-500"
            }`}
            data-testid="reset-view"
            title={t("viewerTab.resetView")}
          >
            <RotateCcw size={11}/> Reset
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        style={{ height, position: "relative" }}
        className={isLight ? "bg-slate-100" : "bg-slate-950"}
        data-testid="step-3d-canvas"
      >
        {status === "loading" && (
          <div className={`absolute inset-0 flex flex-col items-center justify-center z-10 gap-3 ${isLight ? "bg-white/80" : "bg-slate-950/80"}`}>
            <Loader2 size={28} className={isLight ? "animate-spin text-blue-600" : "animate-spin text-cyan-400"}/>
            <div className={`text-xs font-mono ${isLight ? "text-slate-600" : "text-slate-400"}`}>Parsing STEP via OpenCascade WASM…</div>
          </div>
        )}
        {status === "error" && (
          <div className={`absolute inset-0 flex flex-col items-center justify-center z-10 gap-3 p-6 text-center ${isLight ? "bg-white/90" : "bg-slate-950/90"}`}>
            <AlertTriangle size={28} className="text-amber-500"/>
            <div className="text-sm text-amber-600 font-semibold">{t("viewerTab.load3dError")}</div>
            <div className={`text-xs font-mono max-w-md ${isLight ? "text-slate-500" : "text-slate-400"}`}>{error}</div>
            <button
              onClick={loadModel}
              className={`mt-2 px-3 py-1.5 rounded border text-xs ${
                isLight
                  ? "border-slate-300 text-slate-700 hover:border-slate-500"
                  : "border-slate-600 text-slate-300 hover:border-slate-400"
              }`}
              data-testid="retry-3d-load"
            >
              {t("common.retry")}
            </button>
          </div>
        )}
      </div>
      {status === "ready" && holes.length > 0 && showOverlay && (
        <div className={`px-5 py-2 border-t text-[10px] font-mono flex items-center gap-2 ${
          isLight ? "border-slate-200 text-slate-500 bg-slate-50" : "border-slate-800 text-slate-500"
        }`}>
          <span className={`w-2 h-2 rounded-full ${isLight ? "bg-blue-500" : "bg-cyan-400"}`}/>
          {t("viewerTab.dfmLegend")}
        </div>
      )}
    </div>
  );
}
